/**useComponentUtils.js
 * Заменяет методы MixinConverter, связанные с компонентами.
 */
import { formatString } from '@/core/utils';
import EventBus from '@/core/EventBus';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

export function useComponentUtils() {
  const store = useStore();
  const router = useRouter();

  // Поиск компонента по типу и id
  function componentByTypeId(components, type, id) {
    for (const item of components) {
      if (item.id === id && item.typeName === type) return item;
    }
    store.dispatch("addScriptLog", {
      message: `Компонент не найден componentByTypeId: ${type}${id}`,
      facilityId: router.currentRoute.value.params.facility_id
    });
    return null;
  }

  // Поиск компонента по id
  function componentById(components, id) {
    for (const item of components) {
      if (item.id === id) return item;
    }
    store.dispatch("addScriptLog", {
      message: `Компонент не найден componentById: ${id}`,
      facilityId: router.currentRoute.value.params.facility_id
    });
    return null;
  }

  // Поиск компонента по имени (typeName + id)
  function componentByName(components, name) {
    for (const item of components) {
      if (item.typeName + item.id === name) {
        return item;
      }
    }
    store.dispatch("addScriptLog", {
      message: `Компонент не найден componentByName: ${name}`,
      facilityId: router.currentRoute.value.params.facility_id
    });
    return null;
  }

  // Поиск компонента по tag
  function componentByTag(components, tag, type = null) {
    for (const item of components) {
      if (item.attrList?.tag === tag) {
        if (type) {
          if (type === item.typeName) return item;
        } else return item;
      }
    }
    store.dispatch("addScriptLog", {
      message: `Компонент не найден tag: ${tag}`,
      facilityId: router.currentRoute.value.params.facility_id
    });
    return null;
  }

  // Создание таблицы компонентов
  function createTable(components, typeName, cols, rows, { x, y, width, height }, attrList = {}, isDontSave = true) {
    let elem;
    components.forEach((component) => {
      if (component.typeName === typeName) {
        elem = { ...component };
        elem.attrList = { ...component.attrList };
        elem.isDontSave = isDontSave;
      }
    });

    let result = [];
    if (elem) {
      let coordX = x ?? 100;
      let coordY = y ?? 60;
      elem.attrList.width = width ?? elem.default?.width;
      elem.attrList.height = height ?? elem.default?.height;
      delete elem.default;

      for (let col = 0; col < cols; col++) {
        result.push([]);
        for (let row = 0; row < rows; row++) {
          elem = { ...elem };
          elem.attrList = { ...elem.attrList, ...attrList };
          elem.attrList.tag = elem.attrList.tag
            ? formatString(elem.attrList.tag, col, row)
            : '';
          elem.attrList.x = coordX;
          elem.attrList.y = coordY;
          result[col].push(elem);
          EventBus.emit("on-create-component", elem);
          coordY = coordY + elem.attrList.height - 1;
        }
        coordX = coordX + elem.attrList.width - 1;
        coordY = y ?? 60;
      }
    }
    return result;
  }

  // Поиск дочерних объектов
  function getChild(facilityId, flangeNumber, prop = null) {
    let result = [];
    for (const element of store.getters.struct) {
      if (facilityId === element.facility_id) {
        element.children.forEach((child) => {
          if (child.parent_flange_no_min == flangeNumber) {
            if (prop) result.push(child[prop]);
            else result.push(child);
          }
        });
      }
    }
    return result;
  }

  // Подготовка URI для свойств
  function prepareURI(props, name, flange) {
    return props
      .map((item) => ({
        ...item,
        PropertyURI: item[name] + ":фл=" + String(item[flange]),
      }))
      .sort((a, b) =>
        a.PropertyURI > b.PropertyURI
          ? 1
          : b.PropertyURI > a.PropertyURI
            ? -1
            : 0
      );
  }

  // Подготовка данных URI в виде объекта
  function prepareURIData(props, name, flange, flName = 'фл=') {
    const properties = {};
    props.forEach((item) => {
      properties[item[name] + ":" + flName + item[flange]] = item;
    });
    return properties;
  }

  // Проверка бита в значении
  function getBit(value, number) {
    return Boolean(value && (value & (number === 0 ? 1 : 1 << number)));
  }

  // Стиль группы событий
  function styleEventGroup(event) {
    if (event.eventType?.color) {
      return toColor(event.eventType.color);
    }
    return "";
  }

  // Преобразование цвета
  function toColor(color) {
    let fill = "#FFFFFF";
    let hex = color.toString(16);
    if (hex) {
      hex = "#" + hex;
      if (hex.length > 1 && hex.length < 7) {
        const count = 7 - hex.length;
        for (let i = 0; i < count; i++) {
          hex = hex + "0";
        }
      }
      if (hex.length === 7) {
        hex = hex[0] + hex[5] + hex[6] + hex[3] + hex[4] + hex[1] + hex[2];
      }
      fill = hex;
    }
    return fill;
  }

  // Инверсия цвета
  function invertColor(hex) {
    if (hex.indexOf("#") === 0) {
      hex = hex.slice(1);
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const invertedHex = "#" +
      (255 - r).toString(16).padStart(2, '0') +
      (255 - g).toString(16).padStart(2, '0') +
      (255 - b).toString(16).padStart(2, '0');
    return invertedHex;
  }

  // Поиск свойства по событию
  function propertyByEvent(props, event) {
    if (!props) {
      store.dispatch("addScriptLog", {
        message: `Свойства не заданы для метода propertyByEvent: ${event}`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return null;
    }
    for (const prop in props) {
      if (props[prop].id === event.propertyId) {
        return props[prop];
      }
    }
  }

  // Подготовка данных события свойства
  function preparePropertyEventData(events) {
    let event = {
      ["Последнее событие"]: {}
    };
    if (events.length && events.length > 0) {
      event["Последнее событие"] = {
        id: events[0].propertyId,
        dateProperty: events[0].date,
        flangeNo: events[0].flangeNo,
        isVisibleForm: false,
        outputValueColor: events[0]?.eventType?.color,
        outputValueText: `${events[0]?.eventType?.eventCategory.name}; ${events[0].date ? store.getters.typeFormat(new Date(events[0].date), 'date-time') : ''}; ${events[0]?.eventType?.name}`,
      };
    }
    return event;
  }

  // Подготовка события по имени
  function preparePropertyEvent(name) {
    return [{ [name]: "Последнее событие" }];
  }

  // Получение CSS переменной
  function varName(name) {
    const style = getComputedStyle(document.body);
    return style.getPropertyValue(name);
  }

  // Глобальные переменные для скриптов
  const globalVariables = {};
  function global(item, name) {
    globalVariables[name] = item;
  }

  return {
    componentByTypeId,
    componentById,
    componentByName,
    componentByTag,
    createTable,
    getChild,
    prepareURI,
    prepareURIData,
    getBit,
    styleEventGroup,
    toColor,
    invertColor,
    propertyByEvent,
    preparePropertyEventData,
    preparePropertyEvent,
    varName,
    globalVariables,
    global,
    formatString
  };
}
